import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase' // Import for getting session token
import { 
  PhoneIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ChartBarIcon,
  MicrophoneIcon,
  BellIcon,
  ShieldCheckIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  PlayIcon,
  DocumentTextIcon,
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  MapPinIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'

export default function Dashboard() {
  const { userBusinesses, isSuperAdmin, user } = useAuth()
  const [stats, setStats] = useState({
    totalCalls: 0,
    activeCalls: 0,
    emergencyCalls: 0,
    escalationCalls: 0,
    businesses: 0,
    avgResponseTime: 0,
    successRate: 0,
    totalCustomers: 0,
    newCustomers: 0,
    vipCustomers: 0,
    returningCustomers: 0
  })
  
  const [recentCalls, setRecentCalls] = useState([])
  const [businesses, setBusinesses] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h')
  const [currentBusinessPhone, setCurrentBusinessPhone] = useState(null)
  const [selectedCall, setSelectedCall] = useState(null)
  const [showCallModal, setShowCallModal] = useState(false)
  const [error, setError] = useState(null)

  const isSuper = isSuperAdmin()

  useEffect(() => {
    if (!isSuper && userBusinesses && userBusinesses.length > 0) {
      setCurrentBusinessPhone(userBusinesses[0].business_phone)
    }
    
    fetchDashboardData()
    const interval = setInterval(fetchDashboardData, 30000)
    
    return () => clearInterval(interval)
  }, [selectedTimeframe, currentBusinessPhone, userBusinesses, isSuper])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Get auth token from Supabase session
      const { data: { session } } = await supabase.auth.getSession()
      const authToken = session?.access_token
      
      if (!authToken) {
        console.log('🔍 Debug info:')
        console.log('User object:', user)
        console.log('Session:', session)
        console.log('userBusinesses:', userBusinesses)
        throw new Error('No authentication token available. Check console for debug info.')
      }
      
      // Fetch dashboard stats from your Railway backend
      const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'https://sayhi-voice-engine-production.up.railway.app' // Replace with your actual Railway URL
      
      const statsParams = new URLSearchParams({
        timeframe: selectedTimeframe,
        ...(currentBusinessPhone && currentBusinessPhone !== 'all' && { businessPhone: currentBusinessPhone })
      })
      
      const [statsResponse, businessesResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/dashboard/stats?${statsParams}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          }
        }),
        fetch(`${BACKEND_URL}/api/dashboard/businesses`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          }
        })
      ])

      if (!statsResponse.ok) {
        const errorData = await statsResponse.json().catch(() => ({}))
        throw new Error(errorData.error || `Stats API error: ${statsResponse.status}`)
      }
      
      if (!businessesResponse.ok) {
        const errorData = await businessesResponse.json().catch(() => ({}))
        throw new Error(errorData.error || `Businesses API error: ${businessesResponse.status}`)
      }

      const statsData = await statsResponse.json()
      const businessesData = await businessesResponse.json()

      if (!statsData.success) {
        throw new Error(statsData.error || 'Failed to fetch stats')
      }
      
      if (!businessesData.success) {
        throw new Error(businessesData.error || 'Failed to fetch businesses')
      }

      // Update state with API response
      setStats(statsData.stats)
      setRecentCalls(statsData.recentCalls || [])
      setCustomers(statsData.customers || [])
      setBusinesses(businessesData.businesses || [])
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      setError(error.message)
      
      // Set empty state on error
      setStats({
        totalCalls: 0, activeCalls: 0, emergencyCalls: 0, 
        escalationCalls: 0, businesses: 0, avgResponseTime: 0, successRate: 0,
        totalCustomers: 0, newCustomers: 0, vipCustomers: 0, returningCustomers: 0
      })
      setRecentCalls([])
      setBusinesses([])
      setCustomers([])
    } finally {
      setLoading(false)
    }
  }

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Unknown Number'
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.slice(1)
      return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`
    }
    return phone
  }

  const getCallTypeLabel = (call) => {
    if (call.emergency_escalation || call.call_type === 'emergency') {
      return 'Emergency Call'
    }
    if (call.call_type === 'escalation') {
      return 'Escalated Call'
    }
    return 'Regular Call'
  }

  const getCallStatusLabel = (status) => {
    switch(status) {
      case 'completed': return 'Completed Successfully'
      case 'in_progress': return 'Currently Active'
      case 'failed': return 'Call Failed'
      default: return 'Completed'
    }
  }

  const getCallDuration = (call) => {
    const seconds = call.call_duration_seconds || call.duration_seconds || 0
    if (seconds < 60) return `${seconds} seconds`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')} minutes`
  }

  const formatCallTime = (dateString) => {
    if (!dateString) return 'Unknown time'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    
    if (diffMinutes < 60) {
      return `${diffMinutes} minutes ago`
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`
    } else {
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
    }
  }

  const openCallDetails = (call) => {
    setSelectedCall(call)
    setShowCallModal(true)
  }

  const getCallStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityLevel = (call) => {
    if (call.emergency_escalation) return 'urgent'
    if (call.call_type === 'escalation') return 'high'
    return 'normal'
  }

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200'
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200'
      default: return 'text-green-600 bg-green-50 border-green-200'
    }
  }

  const getCustomerTypeLabel = (customer) => {
    if (!customer) return null
    if (customer.vip_status) return 'VIP'
    if (customer.total_calls > 5) return 'Frequent'
    if (customer.total_calls > 1) return 'Returning'
    return 'New'
  }

  const getCustomerTypeColor = (customer) => {
    if (!customer) return ''
    if (customer.vip_status) return 'bg-purple-100 text-purple-800'
    if (customer.total_calls > 5) return 'bg-blue-100 text-blue-800'
    if (customer.total_calls > 1) return 'bg-green-100 text-green-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading your dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <MicrophoneIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {isSuper ? 'Voice Agent Management' : 'My AI Assistant Dashboard'}
                  </h1>
                  <p className="text-sm text-gray-600">
                    {isSuper ? 'Monitor all businesses' : 'Track your customer calls and conversations'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <select 
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="1h">Last Hour</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
              </select>
              
              <button
                onClick={fetchDashboardData}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm transition-all duration-200"
              >
                Refresh
              </button>
            </div>
          </div>
          
          {!isSuper && businesses.length > 0 && (
            <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">
                      {businesses[0]?.business_name?.charAt(0) || 'B'}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{businesses[0]?.business_name}</h3>
                    <p className="text-sm text-gray-600">{businesses[0]?.business_type}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Your Number</p>
                  <p className="font-medium text-gray-900">{formatPhoneNumber(businesses[0]?.phone_number)}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-8">
        {/* Enhanced Stats Grid - 2 rows */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* First Row - Call Stats */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Total Calls</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalCalls}</p>
                <p className="text-sm text-green-600 font-medium">
                  <ArrowTrendingUpIcon className="h-4 w-4 inline mr-1" />
                  Customer interactions
                </p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <PhoneIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Success Rate</p>
                <p className="text-3xl font-bold text-gray-900">{stats.successRate}%</p>
                <p className="text-sm text-green-600 font-medium">
                  Calls handled well
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Urgent Calls</p>
                <p className="text-3xl font-bold text-gray-900">{stats.emergencyCalls}</p>
                <p className="text-sm text-red-600 font-medium">
                  {stats.emergencyCalls === 0 ? 'All handled' : 'Need attention'}
                </p>
              </div>
              <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Active Calls</p>
                <p className="text-3xl font-bold text-gray-900">{stats.activeCalls}</p>
                <p className="text-sm text-orange-600 font-medium">
                  Currently in progress
                </p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <BellIcon className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Second Row - Customer Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Total Customers</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalCustomers}</p>
                <p className="text-sm text-blue-600 font-medium">
                  Customer relationships
                </p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <UserGroupIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">New Customers</p>
                <p className="text-3xl font-bold text-gray-900">{stats.newCustomers}</p>
                <p className="text-sm text-green-600 font-medium">
                  {selectedTimeframe === '1h' ? 'Last hour' : 
                   selectedTimeframe === '24h' ? 'Last 24 hours' :
                   selectedTimeframe === '7d' ? 'Last 7 days' : 'Last 30 days'}
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <ArrowTrendingUpIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">VIP Customers</p>
                <p className="text-3xl font-bold text-gray-900">{stats.vipCustomers}</p>
                <p className="text-sm text-purple-600 font-medium">
                  Premium customers
                </p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <StarIconSolid className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Returning</p>
                <p className="text-3xl font-bold text-gray-900">{stats.returningCustomers}</p>
                <p className="text-sm text-yellow-600 font-medium">
                  Repeat customers
                </p>
              </div>
              <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Recent Calls with Customer Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recent Customer Calls</h3>
              <span className="text-sm text-gray-500">Click any call to see details</span>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentCalls.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <PhoneIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No recent calls</h3>
                <p className="text-gray-500">
                  When customers call your business, their conversations will appear here.
                </p>
              </div>
            ) : (
              recentCalls.map((call, index) => (
                <div 
                  key={call.call_id || index} 
                  className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => openCallDetails(call)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center relative">
                        <PhoneIcon className="h-6 w-6 text-white" />
                        {call.customer?.vip_status && (
                          <div className="absolute -top-1 -right-1">
                            <StarIconSolid className="h-5 w-5 text-yellow-400" />
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <div className="flex items-center space-x-2">
                            {call.customer?.name ? (
                              <p className="text-sm font-medium text-gray-900">
                                {call.customer.name}
                              </p>
                            ) : (
                              <p className="text-sm font-medium text-gray-600">
                                Unknown Customer
                              </p>
                            )}
                            <span className="text-sm text-gray-500">
                              {formatPhoneNumber(call.customer_phone)}
                            </span>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(getPriorityLevel(call))}`}>
                            {getCallTypeLabel(call)}
                          </span>
                          {call.customer && getCustomerTypeLabel(call.customer) && (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCustomerTypeColor(call.customer)}`}>
                              {getCustomerTypeLabel(call.customer)}
                            </span>
                          )}
                        </div>
                        
                        {/* Customer Contact Info */}
                        {call.customer && (call.customer.email || call.customer.address) && (
                          <div className="flex items-center space-x-4 text-xs text-gray-500 mb-1">
                            {call.customer.email && (
                              <div className="flex items-center">
                                <EnvelopeIcon className="h-3 w-3 mr-1" />
                                {call.customer.email}
                              </div>
                            )}
                            {call.customer.address && (
                              <div className="flex items-center">
                                <MapPinIcon className="h-3 w-3 mr-1" />
                                {call.customer.address.length > 40 
                                  ? call.customer.address.substring(0, 40) + '...'
                                  : call.customer.address}
                              </div>
                            )}
                          </div>
                        )}
                        
                        <p className="text-sm text-gray-600">
                          {call.conversation_summary ? 
                            call.conversation_summary.substring(0, 100) + (call.conversation_summary.length > 100 ? '...' : '') : 
                            'Tap to see call details'
                          }
                        </p>
                        {call.services_mentioned && call.services_mentioned.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {call.services_mentioned.slice(0, 3).map((service, i) => (
                              <span key={i} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {service}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{formatCallTime(call.call_start_time)}</p>
                      <p className="text-xs text-gray-400 mt-1">{getCallDuration(call)}</p>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${getCallStatusColor(call.call_status)}`}>
                        {getCallStatusLabel(call.call_status)}
                      </span>
                      {call.customer?.total_calls > 1 && (
                        <p className="text-xs text-blue-600 mt-1">
                          {call.customer.total_calls} total calls
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Call Details Modal */}
      {showCallModal && selectedCall && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-5xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Call Details</h3>
              <button
                onClick={() => setShowCallModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Customer Information */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2" />
                    Customer Information
                  </h4>
                  {selectedCall.customer ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600">Name</label>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedCall.customer.name}
                          {selectedCall.customer.vip_status && (
                            <StarIconSolid className="h-4 w-4 text-purple-500 inline ml-1" />
                          )}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">Phone</label>
                        <p className="text-sm text-gray-900">{formatPhoneNumber(selectedCall.customer_phone)}</p>
                      </div>
                      {selectedCall.customer.email && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Email</label>
                          <p className="text-sm text-gray-900">{selectedCall.customer.email}</p>
                        </div>
                      )}
                      {selectedCall.customer.address && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Address</label>
                          <p className="text-sm text-gray-900">{selectedCall.customer.address}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-xs font-medium text-gray-600">Customer Status</label>
                        <p className="text-sm text-gray-900">
                          {getCustomerTypeLabel(selectedCall.customer)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">Total Calls</label>
                        <p className="text-sm text-gray-900">{selectedCall.customer.total_calls}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <UserIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">No customer information available</p>
                      <p className="text-xs text-gray-500 mt-1">{formatPhoneNumber(selectedCall.customer_phone)}</p>
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Call Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium">{getCallTypeLabel(selectedCall)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium">{getCallDuration(selectedCall)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-medium">{getCallStatusLabel(selectedCall.call_status)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time:</span>
                      <span className="font-medium">{formatCallTime(selectedCall.call_start_time)}</span>
                    </div>
                  </div>
                </div>

                {selectedCall.services_mentioned && selectedCall.services_mentioned.length > 0 && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Services Discussed</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCall.services_mentioned.map((service, i) => (
                        <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Conversation Summary */}
              <div className="md:col-span-2 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                    Call Summary
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    {selectedCall.conversation_summary || 'No summary available for this call.'}
                  </p>
                </div>

                {selectedCall.customer_sentiment && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Customer Satisfaction</h4>
                    <div className="flex items-center space-x-2">
                      <div className={`h-3 w-3 rounded-full ${
                        selectedCall.customer_sentiment === 'positive' ? 'bg-green-400' :
                        selectedCall.customer_sentiment === 'negative' ? 'bg-red-400' : 'bg-yellow-400'
                      }`}></div>
                      <span className="capitalize font-medium">
                        {selectedCall.customer_sentiment || 'Neutral'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Full Transcript */}
                {selectedCall.transcript && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Full Conversation
                    </h4>
                    <div className="max-h-60 overflow-y-auto bg-white rounded border p-3">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                        {selectedCall.transcript}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCallModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}